import { ChevronLeft, ChevronRight, Minus, Plus } from 'lucide-react';
import {
  bookingTotal,
  lineTotalForParticipant,
  type AvailabilitySlot,
  type ExperienceOption,
} from '@flyingmuse/shared';
import {
  dayOf,
  formatDateLabel,
  formatDateLong,
  formatMonthLabel,
  formatPrice,
  formatTimeLabel,
  monthOf,
} from '../format';
import { guestRule } from '../bookingFlow';
import { WhatsAppFallback } from '../WhatsAppFallback';

interface Props {
  option: ExperienceOption;
  /** Slots for the month on screen only. */
  slots: AvailabilitySlot[];
  slotsStatus: 'idle' | 'loading' | 'ready' | 'error';
  /** Every browsable month ('yyyy-MM'), this month through the season's end. */
  months: string[];
  selectedMonth: string;
  onSelectMonth: (month: string) => void;
  selectedDay: string | null;
  selectedDate: string | null;
  /** Resolved separately — the choice may live in a month not on screen. */
  selectedSlot?: AvailabilitySlot;
  quantities: Record<string, number>;
  experienceLabel?: string;
  onSelectDay: (day: string) => void;
  onSelectDate: (date: string) => void;
  onSetQty: (alias: string, quantity: number) => void;
}

export function DateGuestsStep({
  option,
  slots,
  slotsStatus,
  months,
  selectedMonth,
  onSelectMonth,
  selectedDay,
  selectedDate,
  selectedSlot,
  quantities,
  experienceLabel,
  onSelectDay,
  onSelectDate,
  onSetQty,
}: Props) {
  const available = slots.filter((s) => s.vacancies > 0);
  // Distinct calendar days that have at least one open time slot.
  const days = [...new Set(available.map((s) => dayOf(s.dateTime)))].sort();
  // Times are only offered for a day inside the month being browsed.
  const timeSlots =
    selectedDay && monthOf(selectedDay) === selectedMonth
      ? available
          .filter((s) => dayOf(s.dateTime) === selectedDay)
          .sort((a, b) => a.dateTime.localeCompare(b.dateTime))
      : [];
  const monthIndex = months.indexOf(selectedMonth);
  const prevMonth = monthIndex > 0 ? months[monthIndex - 1] : null;
  const nextMonth =
    monthIndex >= 0 && monthIndex < months.length - 1
      ? months[monthIndex + 1]
      : null;
  const totalGuests = Object.values(quantities).reduce((n, q) => n + q, 0);
  const rule = guestRule(option);
  // The rule's minimum / lock applies to the first primary participant type.
  const firstPrimaryAlias = option.participantTypes.find((pt) => pt.isPrimary)?.alias;

  const pricingFor = (alias: string) =>
    selectedSlot?.pricing.find((p) => p.participantTypeAlias === alias);

  return (
    <div>
      <h3 className="font-display text-2xl font-medium text-charcoal">
        Pick a date &amp; time
      </h3>
      <p className="mt-1.5 font-sans text-sm font-light text-stone">
        {option.title}. Choose a day, then a start time.
      </p>

      {/* Month browser */}
      <div className="mt-6 flex items-center justify-between gap-3">
        <p className="font-sans text-[0.62rem] font-semibold uppercase tracking-label text-charcoal/55">
          Available dates
        </p>
        <div className="flex items-center gap-1">
          <MonthArrow
            direction="prev"
            month={prevMonth}
            onSelect={onSelectMonth}
          />
          <span className="min-w-[8.5rem] text-center font-sans text-sm font-medium text-charcoal">
            {formatMonthLabel(selectedMonth)}
          </span>
          <MonthArrow
            direction="next"
            month={nextMonth}
            onSelect={onSelectMonth}
          />
        </div>
      </div>

      {slotsStatus === 'loading' && (
        <p className="mt-3 font-sans text-sm font-light text-stone">
          Loading availability&hellip;
        </p>
      )}
      {slotsStatus === 'error' && (
        <WhatsAppFallback contextLabel={experienceLabel} />
      )}
      {slotsStatus === 'ready' && available.length === 0 && (
        <div className="mt-3 rounded-xl border border-dashed border-charcoal/20 px-4 py-6 text-center">
          <p className="font-sans text-sm font-light text-stone">
            Nothing open in {formatMonthLabel(selectedMonth)}.
            {nextMonth ? ' Try another month.' : ''}
          </p>
          {nextMonth && (
            <button
              type="button"
              onClick={() => onSelectMonth(nextMonth)}
              className="mt-3 font-sans text-xs font-medium uppercase tracking-wide2 text-crimson transition-opacity hover:opacity-70"
            >
              See {formatMonthLabel(nextMonth)}
            </button>
          )}
        </div>
      )}

      {days.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
          {days.map((day) => {
            const active = day === selectedDay;
            return (
              <button
                key={day}
                type="button"
                onClick={() => onSelectDay(day)}
                className={`rounded-xl border px-2 py-2.5 text-center transition-colors ${
                  active
                    ? 'border-crimson bg-crimson text-white'
                    : 'border-charcoal/15 bg-white text-charcoal hover:border-crimson'
                }`}
              >
                <span className="block font-sans text-xs font-medium">
                  {formatDateLabel(day)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Times — shown once a day is chosen */}
      {selectedDay && timeSlots.length > 0 && (
        <>
          <p className="mt-7 font-sans text-[0.62rem] font-semibold uppercase tracking-label text-charcoal/55">
            Start time
          </p>
          <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
            {timeSlots.map((slot) => {
              const active = slot.dateTime === selectedDate;
              const time = formatTimeLabel(slot.dateTime) ?? '—';
              return (
                <button
                  key={slot.dateTime}
                  type="button"
                  onClick={() => onSelectDate(slot.dateTime)}
                  className={`rounded-xl border px-2 py-2.5 text-center font-sans text-sm font-medium transition-colors ${
                    active
                      ? 'border-crimson bg-crimson text-white'
                      : 'border-charcoal/15 bg-white text-charcoal hover:border-crimson'
                  }`}
                >
                  {time}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Guests */}
      {selectedSlot && (
        <div className="mt-7">
          {/* Restated because the choice can sit in a month the guest has
              paged away from — otherwise the panel below looks unanchored. */}
          <p className="font-sans text-xs font-light text-stone">
            Selected:{' '}
            <span className="font-medium text-charcoal">
              {formatDateLong(selectedSlot.dateTime)}
              {formatTimeLabel(selectedSlot.dateTime)
                ? ` · ${formatTimeLabel(selectedSlot.dateTime)}`
                : ''}
            </span>
          </p>
          <p className="mt-4 font-sans text-[0.62rem] font-semibold uppercase tracking-label text-charcoal/55">
            Guests
          </p>
          {rule.locked ? (
            <p className="mt-1.5 font-sans text-xs font-light text-stone">
              This package is always booked for two guests.
            </p>
          ) : rule.min > 1 ? (
            <p className="mt-1.5 font-sans text-xs font-light text-stone">
              This package requires at least {rule.min} guests.
            </p>
          ) : null}
          <ul className="mt-3 flex flex-col divide-y divide-charcoal/10 rounded-xl border border-charcoal/15">
            {option.participantTypes.map((pt) => {
              const qty = quantities[pt.alias] ?? 0;
              const isRuleType = pt.alias === firstPrimaryAlias;
              const baseMin = pt.isPrimary ? Math.max(1, pt.minOrder) : 0;
              const min = isRuleType ? Math.max(baseMin, rule.min) : baseMin;
              const locked = rule.locked && isRuleType;
              const usedByOthers = pt.hasSharedAvailability
                ? totalGuests - qty
                : 0;
              const max = locked
                ? rule.min
                : Math.max(min, selectedSlot.vacancies - usedByOthers);
              const perPerson = lineTotalForParticipant(pricingFor(pt.alias), 1);
              return (
                <li
                  key={pt.alias}
                  className="flex items-center justify-between gap-3 px-4 py-3"
                >
                  <div>
                    <p className="font-sans text-sm font-medium text-charcoal">
                      {pt.name}
                    </p>
                    {perPerson > 0 && (
                      <p className="font-sans text-xs font-light text-stone">
                        {formatPrice(perPerson)} each
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      aria-label={`Decrease ${pt.name}`}
                      disabled={qty <= min}
                      onClick={() => onSetQty(pt.alias, qty - 1)}
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-charcoal/20 text-charcoal transition-colors hover:border-crimson hover:text-crimson disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="w-6 text-center font-sans text-sm font-medium text-charcoal">
                      {qty}
                    </span>
                    <button
                      type="button"
                      aria-label={`Increase ${pt.name}`}
                      disabled={qty >= max}
                      onClick={() => onSetQty(pt.alias, qty + 1)}
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-charcoal/20 text-charcoal transition-colors hover:border-crimson hover:text-crimson disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="mt-4 flex items-center justify-between">
            <span className="font-sans text-[0.7rem] font-medium uppercase tracking-wide2 text-charcoal/55">
              Total
            </span>
            <span className="font-display text-2xl font-medium text-charcoal">
              {formatPrice(bookingTotal(selectedSlot, quantities))}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/** Month pager arrow. Rendered disabled at the ends so the row never reflows. */
function MonthArrow({
  direction,
  month,
  onSelect,
}: {
  direction: 'prev' | 'next';
  month: string | null;
  onSelect: (month: string) => void;
}) {
  const Icon = direction === 'prev' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      disabled={!month}
      onClick={() => month && onSelect(month)}
      aria-label={
        month
          ? `${direction === 'prev' ? 'Previous' : 'Next'} month, ${formatMonthLabel(month)}`
          : `No ${direction === 'prev' ? 'earlier' : 'later'} months`
      }
      className="flex h-8 w-8 items-center justify-center rounded-full border border-charcoal/15 text-charcoal transition-colors hover:border-crimson hover:text-crimson disabled:cursor-not-allowed disabled:border-charcoal/10 disabled:text-charcoal/25 disabled:hover:border-charcoal/10 disabled:hover:text-charcoal/25"
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
